/**
 * Debounce utility pro optimalizaci casto volanych funkci
 * OPTIMALIZACE: Snizuje pocet zbytecnych volani (napr. pano-change event)
 */

type AnyFunction = (...args: any[]) => any;

interface DebouncedFunction<T extends AnyFunction> {
    (...args: Parameters<T>): void;
    cancel: () => void;
}

/**
 * Debounce funkce - odlozi volani funkce o dany cas
 * Pokud je funkce zavolana znovu behem cekani, casovac se resetuje
 *
 * @param func - Funkce k debounce
 * @param wait - Cas cekani v ms
 * @returns Debounced verze funkce
 */
export function debounce<T extends AnyFunction>(
    func: T,
    wait: number,
): DebouncedFunction<T> {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const debouncedFn = function (this: any, ...args: Parameters<T>) {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
            func.apply(this, args);
            timeoutId = null;
        }, wait);
    } as DebouncedFunction<T>;

    debouncedFn.cancel = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    };

    return debouncedFn;
}

/**
 * Throttle funkce - omezi volani funkce na maximalne jednou za dany cas
 *
 * @param func - Funkce k throttle
 * @param wait - Minimalni cas mezi volanimi v ms
 * @returns Throttled verze funkce
 */
export function throttle<T extends AnyFunction>(
    func: T,
    wait: number,
): DebouncedFunction<T> {
    let lastTime = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const throttledFn = function (this: any, ...args: Parameters<T>) {
        const now = Date.now();

        if (now - lastTime >= wait) {
            lastTime = now;
            func.apply(this, args);
        } else if (!timeoutId) {
            // Naplanovame volani na konec intervalu
            timeoutId = setTimeout(() => {
                lastTime = Date.now();
                func.apply(this, args);
                timeoutId = null;
            }, wait - (now - lastTime));
        }
    } as DebouncedFunction<T>;

    throttledFn.cancel = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    };

    return throttledFn;
}
